import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Play, CheckCircle, XCircle, AlertTriangle, AlertCircle, Loader, Circle, RotateCw } from 'lucide-react';
import Button from '../../../../components/ui/Button';

interface WorkflowNode {
  id: string;
  type: string;
  label: string;
}

interface WorkflowConnection {
  id: string;
  source: string;
  target: string;
}

interface Workflow {
  nodes: WorkflowNode[];
  connections: WorkflowConnection[];
}

interface TestResults {
  status: string;
  duration: string;
  stepsExecuted: number;
  errors: string[];
  warnings: string[];
}

interface WorkflowTestModalProps {
  workflow: Workflow;
  onClose: () => void;
}

const WorkflowTestModal = ({ workflow, onClose }: WorkflowTestModalProps) => {
  const { t } = useTranslation();
  const [testResults, setTestResults] = useState<TestResults | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const runTest = () => {
    setIsRunning(true);
    setCurrentStep(0);

    // Simulate workflow execution
    const interval = setInterval(() => {
      setCurrentStep(prev => {
        if (prev >= workflow?.nodes?.length - 1) {
          clearInterval(interval);
          setIsRunning(false);
          setTestResults({
            status: 'success',
            duration: '2.3s',
            stepsExecuted: workflow?.nodes?.length,
            errors: [],
            warnings: [
              'Node "Manager Approval" has no timeout configured',
              'Consider adding error handling for "Send Email" task'
            ]
          });
          return prev;
        }
        return prev + 1;
      });
    }, 500);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{t('Test Workflow')}</h2>
            <p className="text-sm text-gray-600">{t('Simulate workflow execution and validate logic')}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Workflow Summary */}
          <div className="bg-muted border border-border rounded-lg p-4 mb-6">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-gray-900">{workflow?.nodes?.length}</div>
                <div className="text-sm text-gray-600">{t('Total Nodes')}</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{workflow?.connections?.length}</div>
                <div className="text-sm text-gray-600">{t('Connections')}</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {workflow?.nodes?.filter(n => n?.type === 'decision')?.length}
                </div>
                <div className="text-sm text-gray-600">{t('Decision Points')}</div>
              </div>
            </div>
          </div>

          {/* Test Execution */}
          {!testResults && !isRunning && (
            <div className="text-center py-8">
              <Play className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('Ready to Test')}</h3>
              <p className="text-sm text-gray-600 mb-6">
                {t('Click the button below to simulate workflow execution')}
              </p>
              <Button variant="default" iconName="Play" onClick={runTest}>
                {t('Run Test')}
              </Button>
            </div>
          )}

          {/* Running State */}
          {isRunning && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-blue-600">
                <div className="animate-spin">
                  <Loader className="w-5 h-5" />
                </div>
                <span className="font-medium">{t('Executing workflow...')}</span>
              </div>
              <div className="space-y-2">
                {workflow?.nodes?.map((node, index) => (
                  <div
                    key={node?.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border ${
                      index < currentStep
                        ? 'bg-green-50 border-green-200'
                        : index === currentStep
                        ? 'bg-blue-50 border-blue-200' : 'bg-muted border-border'
                    }`}
                  >
                    {index < currentStep ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : index === currentStep ? (
                      <div className="animate-spin">
                        <Loader className="w-5 h-5 text-blue-600" />
                      </div>
                    ) : (
                      <Circle className="w-5 h-5 text-gray-400" />
                    )}
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{node?.label}</div>
                      <div className="text-sm text-gray-600">{node?.type}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Test Results */}
          {testResults && (
            <div className="space-y-4">
              <div className={`flex items-center gap-3 p-4 rounded-lg ${
                testResults?.status === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
              }`}>
                {testResults?.status === 'success' ? (
                  <CheckCircle className="w-6 h-6 text-green-600" />
                ) : (
                  <XCircle className="w-6 h-6 text-red-600" />
                )}
                <div>
                  <div className="font-semibold text-gray-900">
                    {testResults?.status === 'success' ? t('Test Passed') : t('Test Failed')}
                  </div>
                  <div className="text-sm text-gray-600">
                    {t('Completed in')} {testResults?.duration} &middot; {testResults?.stepsExecuted} {t('steps executed')}
                  </div>
                </div>
              </div>

              {/* Warnings */}
              {testResults?.warnings?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    {t('Warnings')} ({testResults?.warnings?.length})
                  </h4>
                  {testResults?.warnings?.map((warning, index) => (
                    <div key={index} className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
                      <span className="text-sm text-amber-900">{warning}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Errors */}
              {testResults?.errors?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-red-600" />
                    {t('Errors')} ({testResults?.errors?.length})
                  </h4>
                  {testResults?.errors?.map((error, index) => (
                    <div key={index} className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
                      <span className="text-sm text-red-900">{error}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          {testResults && (
            <Button
              variant="outline"
              iconName="RotateCw"
              onClick={() => {
                setTestResults(null);
                runTest();
              }}
            >
              {t('Run Again')}
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            {t('Close')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WorkflowTestModal;
